import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Grid,
  Switch,
  FormControlLabel,
} from '@mui/material';
import SearchableSelect from '../../components/SearchableSelect';
import FiltroSucursalVentaSelect from '../../components/shared/FiltroSucursalVentaSelect';
import { useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import { useFechasVentaFiltro } from '../../hooks/useFechasVentaFiltro';
import SearchIcon from '@mui/icons-material/Search';
import SpeedIcon from '@mui/icons-material/Speed';
import { ROUTES } from '../../config/routes';
import PrintIcon from '@mui/icons-material/Print';
import InventoryIcon from '@mui/icons-material/Inventory';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import ReceiptIcon from '@mui/icons-material/Receipt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';
import { facturaPosPath } from '../../config/routes';
import { useSnackbar } from 'notistack';
import pedidoVentaService, { PedidoVenta } from '../../services/pedidoVentaService';
import PedidoDetalle from './components/PedidoDetalle';
import PDFViewer from '../../components/shared/PDFViewer';
import { formatCurrency } from '../../utils/formatters';
import { formatFechaVencimDisplay } from '../../utils/facturaVentaFechas';
import { getCookie, setCookie } from '../../utils/cookies';
import { getApiErrorMessage } from '../../utils/apiError';

const Pedidos: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const { fechaDesde, setFechaDesde, fechaHasta, setFechaHasta } = useFechasVentaFiltro();

  const [items, setItems] = useState<PedidoVenta[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<string>('');
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales } = useSucursalesVentaFiltro();
  const [filtroPendientesFacturar, setFiltroPendientesFacturar] = useState<boolean>(() => {
    const cookieValue = getCookie('pedidos_filtro_pendientes_facturar');
    return cookieValue === 'true';
  });
  const [detalleDialogOpen, setDetalleDialogOpen] = useState(false);
  const [selectedPedidoId, setSelectedPedidoId] = useState<number | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedPedido, setSelectedPedido] = useState<number | null>(null);
  const [pdfLoading, setPdfLoading] = useState<number | null>(null);
  const [kebabAnchor, setKebabAnchor] = useState<{ el: HTMLElement; pedidoId: number } | null>(null);

  const fetchPedidos = useCallback(async (
    pageNumber: number,
    pageSize: number,
    search: string,
    estado: string,
    desde: string,
    hasta: string,
    pendientesFacturar: boolean,
    nroSuc: number | null
  ) => {
    try {
      const validPageSize = Math.max(1, Math.min(20, pageSize));
      const params: Parameters<typeof pedidoVentaService.getPedidosVenta>[0] = {
        pageNumber: pageNumber + 1,
        pageSize: validPageSize,
        searchTerm: search || undefined,
        fechaDesde: desde ? new Date(desde) : undefined,
        fechaHasta: hasta ? new Date(hasta) : undefined,
        PendienteFacturar: pendientesFacturar || undefined,
        nroSuc: nroSuc != null && nroSuc > 0 ? nroSuc : undefined,
      };
      if (estado) params.estado = estado;
      const response = await pedidoVentaService.getPedidosVenta(params);
      setItems(response.items);
      setTotalRecords(response.totalCount);
    } catch (error: any) {
      const message = getApiErrorMessage(error, 'Error al cargar los pedidos');
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchPedidos(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroPendientesFacturar, filtroNroSuc);
  }, [page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroPendientesFacturar, filtroNroSuc, fetchPedidos]);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const validRowsPerPage = Math.max(1, Math.min(20, parseInt(event.target.value, 10)));
    setRowsPerPage(validRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleNuevoPedido = () => navigate(ROUTES.ventas.pedidos.pos);

  const handleComprobanteClick = (pedido: PedidoVenta) => {
    if (pedido.estado === 'Nuevo') {
      navigate(`/ventas/pedidos/${pedido.nroFormulario}/editar`);
    } else {
      navigate(`/ventas/pedidos/${pedido.nroFormulario}/ver`);
    }
  };

  const handleCloseDetalle = () => {
    setDetalleDialogOpen(false);
    setSelectedPedidoId(null);
  };

  const handleKebabOpen = (event: React.MouseEvent<HTMLElement>, pedidoId: number) => {
    event.stopPropagation();
    setKebabAnchor({ el: event.currentTarget, pedidoId });
  };

  const handleKebabClose = () => setKebabAnchor(null);

  const handleFacturar = (pedidoId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(facturaPosPath({ pedidoId }));
  };

  const handleMarcarImpreso = async (pedidoId: number) => {
    handleKebabClose();
    try {
      await pedidoVentaService.marcarImpreso(pedidoId);
      enqueueSnackbar('Pedido marcado como impreso', { variant: 'success' });
      fetchPedidos(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroPendientesFacturar, filtroNroSuc);
    } catch (error: any) {
      const message = error?.response?.data || 'Error al marcar el pedido como impreso';
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
    }
  };

  const handleMarcarListoPicking = async (pedidoId: number) => {
    handleKebabClose();
    try {
      await pedidoVentaService.marcarListoPicking(pedidoId);
      enqueueSnackbar('Pedido enviado a picking', { variant: 'success' });
      fetchPedidos(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroPendientesFacturar, filtroNroSuc);
    } catch (error: any) {
      const message = error?.response?.data || 'Error al enviar el pedido a picking';
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
    }
  };

  const handleMarcarPreparado = async (pedidoId: number) => {
    handleKebabClose();
    try {
      await pedidoVentaService.marcarPreparado(pedidoId);
      enqueueSnackbar('Pedido marcado como preparado', { variant: 'success' });
      fetchPedidos(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroPendientesFacturar, filtroNroSuc);
    } catch (error: any) {
      const message = error?.response?.data || 'Error al marcar el pedido como preparado';
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
    }
  };

  const handleAnular = async (pedidoId: number) => {
    handleKebabClose();
    if (window.confirm('¿Está seguro que desea anular este pedido?')) {
      try {
        await pedidoVentaService.anularPedido(pedidoId);
        enqueueSnackbar('Pedido anulado correctamente', { variant: 'success' });
        fetchPedidos(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroPendientesFacturar, filtroNroSuc);
      } catch (error: any) {
        const message = error?.response?.data || 'Error al anular el pedido';
        enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
      }
    }
  };

  const handleImprimirPDF = async (pedidoId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      setPdfLoading(pedidoId);
      const blob = await pedidoVentaService.getPedidoVentaPDF(pedidoId);
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setSelectedPedido(pedidoId);
    } catch (error: any) {
      const message = error?.response?.data || 'Error al generar el PDF del pedido';
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
    } finally {
      setPdfLoading(null);
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setSelectedPedido(null);
  };

  const calcularTotal = (pedido: PedidoVenta): number => {
    if (pedido.importeTotal !== undefined && pedido.importeTotal !== null) {
      return pedido.importeTotal;
    }
    return pedido.detalles?.reduce((sum, d) => {
      const subtotal = d.subtotal || (d.precioUnitario || 0) * (d.cantidad || 0);
      return sum + subtotal;
    }, 0) || 0;
  };

  const puedeFacturar = (pedido: PedidoVenta): boolean =>
    pedido.estado !== 'Facturado' && pedido.estado !== 'Listo para pickear';

  const permiteAcciones = (pedido: PedidoVenta): boolean =>
    pedido.estado !== 'Facturado' && pedido.estado !== 'Anulado';

  const puedeImprimir = (pedido: PedidoVenta): boolean =>
    (permiteAcciones(pedido) && pedido.asentado !== 'P' && pedido.asentado !== 'K') ||
    pedido.estado === 'Facturado';

  const getEstadoChip = (pedido: PedidoVenta) => {
    const estado = pedido.estado || 'Nuevo';
    const estadoMap: Record<string, { label: string; bgColor: string; textColor: string }> = {
      'Nuevo':              { label: 'Nuevo',              bgColor: '#FFF9C4', textColor: '#856404' },
      'Impreso':            { label: 'Impreso',            bgColor: '#E3F2FD', textColor: '#1565C0' },
      'Listo para pickear': { label: 'Listo para pickear', bgColor: '#BBDEFB', textColor: '#1565C0' },
      'Preparado':          { label: 'Preparado',          bgColor: '#B3E5FC', textColor: '#01579B' },
      'Facturado':          { label: 'Facturado',          bgColor: '#C8E6C9', textColor: '#2E7D32' },
      'Anulado':            { label: 'Anulado',            bgColor: '#FFCDD2', textColor: '#C62828' },
    };
    return estadoMap[estado] || { label: estado, bgColor: '#FFF9C4', textColor: '#856404' };
  };

  const formatComprobante = (pedido: PedidoVenta) => {
    const letra = (pedido as any).letra;
    const nroSuc = (pedido as any).nroSuc;
    const nroComprobante = (pedido as any).nroComprobante;

    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) {
      return '-';
    }

    const formatPart = (value: number | string, length: number) => {
      if (value === null || value === undefined) return ''.padStart(length, '0');
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) return numeric.toString().padStart(length, '0');
      return String(value).padStart(length, '0');
    };

    const letraNormalizada = letra.trim().toUpperCase();
    if (!letraNormalizada) return '-';
    return `${letraNormalizada}-${formatPart(nroSuc, 5)}-${formatPart(nroComprobante, 8)}`;
  };

  const btnSx = (bgColor: string, hoverColor: string) => ({
    bgcolor: bgColor,
    color: '#fff',
    borderRadius: 1.5,
    width: 32,
    height: 32,
    '&:hover': { bgcolor: hoverColor, transform: 'scale(1.1)' },
    '&:disabled': { bgcolor: '#ccc' },
    transition: 'all 0.2s'
  });

  const comprobanteSx = {
    cursor: 'pointer',
    color: 'primary.main',
    fontWeight: 500,
    '&:hover': { textDecoration: 'underline' }
  };

  const currentKebabPedido = kebabAnchor
    ? items.find(p => p.nroFormulario === kebabAnchor.pedidoId)
    : null;

  const renderKebabItems = (pedido: PedidoVenta) => {
    const menuItems = [];
    if (pedido.asentado !== 'P' && pedido.asentado !== 'K' && pedido.asentado !== 'I') {
      menuItems.push(
        <MenuItem key="impreso" onClick={() => handleMarcarImpreso(pedido.nroFormulario)}>
          <ListItemIcon><InventoryIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Marcar como impreso</ListItemText>
        </MenuItem>
      );
    }
    if (pedido.asentado !== 'P' && pedido.asentado !== 'K') {
      menuItems.push(
        <MenuItem key="picking" onClick={() => handleMarcarListoPicking(pedido.nroFormulario)}>
          <ListItemIcon><InventoryIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Enviar a picking</ListItemText>
        </MenuItem>
      );
    }
    if (pedido.estado !== 'Preparado') {
      menuItems.push(
        <MenuItem key="preparado" onClick={() => handleMarcarPreparado(pedido.nroFormulario)}>
          <ListItemIcon><CheckCircleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Marcar preparado</ListItemText>
        </MenuItem>
      );
    }
    menuItems.push(
      <MenuItem key="anular" onClick={() => handleAnular(pedido.nroFormulario)} sx={{ color: 'error.main' }}>
        <ListItemIcon><BlockIcon fontSize="small" color="error" /></ListItemIcon>
        <ListItemText>Anular pedido</ListItemText>
      </MenuItem>
    );
    return menuItems;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>

            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Pedidos
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SpeedIcon />}
                onClick={handleNuevoPedido}
                sx={{
                  borderRadius: 2,
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': { boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)' }
                }}
              >
                Nuevo Pedido POS
              </Button>
            </Stack>

            {/* Filtros */}
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} lg={3}>
                  <TextField
                    fullWidth
                    placeholder="Buscar por número, nombre o CUIT..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#666' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <SearchableSelect
                    label="Estado"
                    allowEmpty
                    emptyOptionLabel="Todos"
                    value={filtroEstado || null}
                    onChange={(v) => { setFiltroEstado(v ?? ''); setPage(0); }}
                    options={[
                      { value: 'Nuevo', label: 'Nuevo' },
                      { value: 'Impreso', label: 'Impreso' },
                      { value: 'Listo para pickear', label: 'Listo para pickear' },
                      { value: 'Preparado', label: 'Preparado' },
                      { value: 'Facturado', label: 'Facturado' },
                      { value: 'Anulado', label: 'Anulado' },
                    ]}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <TextField
                    fullWidth
                    label="Fecha Desde"
                    type="date"
                    value={fechaDesde}
                    onChange={(e) => { setFechaDesde(e.target.value); setPage(0); }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <TextField
                    fullWidth
                    label="Fecha Hasta"
                    type="date"
                    value={fechaHasta}
                    onChange={(e) => { setFechaHasta(e.target.value); setPage(0); }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={2}>
                  <FiltroSucursalVentaSelect
                    value={filtroNroSuc}
                    onChange={(v) => { setFiltroNroSuc(v); setPage(0); }}
                    loading={loadingSucursales}
                    sucursales={sucursales}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={2}>
                  <FormControlLabel
                    sx={{
                      m: 0,
                      height: '100%',
                      alignItems: 'center',
                      whiteSpace: { lg: 'nowrap' },
                    }}
                    control={
                      <Switch
                        size="small"
                        color="primary"
                        checked={filtroPendientesFacturar}
                        onChange={(e) => {
                          const newValue = e.target.checked;
                          setFiltroPendientesFacturar(newValue);
                          setCookie('pedidos_filtro_pendientes_facturar', newValue.toString());
                          setPage(0);
                        }}
                      />
                    }
                    label={
                      <>
                        <Box component="span" sx={{ display: { xs: 'inline', lg: 'none' } }}>
                          Pend. de Facturar
                        </Box>
                        <Box component="span" sx={{ display: { xs: 'none', lg: 'inline' } }}>
                          Pend. fact.
                        </Box>
                      </>
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Vista Mobile */}
            {isMobile ? (
              <Box>
                {items.map((pedido) => {
                  const estadoChip = getEstadoChip(pedido);
                  return (
                    <Card
                      key={pedido.nroFormulario}
                      sx={{ mb: 2, borderRadius: 2, border: '1px solid #e0e0e0', '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' } }}
                    >
                      <CardContent>
                        <Stack spacing={1.5}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography
                              component="span"
                              variant="subtitle1"
                              sx={{ ...comprobanteSx, fontWeight: 600 }}
                              onClick={() => handleComprobanteClick(pedido)}
                            >
                              Pedido #{pedido.nroFormulario}
                            </Typography>
                            <Chip
                              label={estadoChip.label}
                              size="small"
                              sx={{ bgcolor: estadoChip.bgColor, color: estadoChip.textColor, fontWeight: 500, borderRadius: 1.5 }}
                            />
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            Fecha: {new Date(pedido.fechaEmision).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Vencimiento: {formatFechaVencimDisplay(pedido.fechaVencim ?? pedido.fechaEmision)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Cliente: {pedido.nombre}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                            Importe Total: {formatCurrency(calcularTotal(pedido))}
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            {puedeImprimir(pedido) && (
                              <Tooltip title="Imprimir PDF">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleImprimirPDF(pedido.nroFormulario, e)}
                                  disabled={pdfLoading === pedido.nroFormulario}
                                  sx={btnSx('#2196f3', '#1976d2')}
                                >
                                  {pdfLoading === pedido.nroFormulario
                                    ? <CircularProgress size={16} sx={{ color: '#fff' }} />
                                    : <PrintIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                            {puedeFacturar(pedido) && permiteAcciones(pedido) && (
                              <Tooltip title="Facturar pedido">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleFacturar(pedido.nroFormulario, e)}
                                  sx={btnSx('#4caf50', '#45a049')}
                                >
                                  <ReceiptIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {permiteAcciones(pedido) && (
                              <Tooltip title="Más acciones">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleKebabOpen(e, pedido.nroFormulario)}
                                  sx={{ borderRadius: 1.5, width: 32, height: 32 }}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
                <TablePagination
                  rowsPerPageOptions={[5, 10, 20]}
                  component="div"
                  count={totalRecords}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Filas por página"
                />
              </Box>
            ) : (
              /* Vista Desktop */
              <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Nro. Pedido</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Vencimiento</TableCell>
                      <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 160, whiteSpace: 'nowrap' }}>Comprobante</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Importe Total</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((pedido) => {
                      const estadoChip = getEstadoChip(pedido);
                      return (
                        <TableRow
                          key={pedido.nroFormulario}
                          sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}
                        >
                          <TableCell sx={{ fontWeight: 500 }}>{pedido.nroFormulario}</TableCell>
                          <TableCell>{new Date(pedido.fechaEmision).toLocaleDateString()}</TableCell>
                          <TableCell>{formatFechaVencimDisplay(pedido.fechaVencim ?? pedido.fechaEmision)}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>
                            <Typography component="span" sx={comprobanteSx} onClick={() => handleComprobanteClick(pedido)}>
                              {formatComprobante(pedido)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{pedido.nombre}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                            {formatCurrency(calcularTotal(pedido))}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={estadoChip.label}
                              size="small"
                              sx={{ bgcolor: estadoChip.bgColor, color: estadoChip.textColor, fontWeight: 500, borderRadius: 1.5, minWidth: 90 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              {puedeImprimir(pedido) && (
                                <Tooltip title="Imprimir PDF">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleImprimirPDF(pedido.nroFormulario, e)}
                                    disabled={pdfLoading === pedido.nroFormulario}
                                    sx={btnSx('#2196f3', '#1976d2')}
                                  >
                                    {pdfLoading === pedido.nroFormulario
                                      ? <CircularProgress size={16} sx={{ color: '#fff' }} />
                                      : <PrintIcon fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                              )}
                              {puedeFacturar(pedido) && permiteAcciones(pedido) && (
                                <Tooltip title="Facturar pedido">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleFacturar(pedido.nroFormulario, e)}
                                    sx={btnSx('#4caf50', '#45a049')}
                                  >
                                    <ReceiptIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {permiteAcciones(pedido) && (
                                <Tooltip title="Más acciones">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleKebabOpen(e, pedido.nroFormulario)}
                                    sx={{ borderRadius: 1.5, width: 32, height: 32 }}
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 20]}
                  component="div"
                  count={totalRecords}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Filas por página"
                  sx={{ borderTop: '1px solid #e0e0e0' }}
                />
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Menú kebab compartido */}
        <Menu
          anchorEl={kebabAnchor?.el}
          open={Boolean(kebabAnchor)}
          onClose={handleKebabClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {currentKebabPedido && renderKebabItems(currentKebabPedido)}
        </Menu>

        <PedidoDetalle
          open={detalleDialogOpen}
          onClose={handleCloseDetalle}
          pedidoId={selectedPedidoId}
        />

        {pdfUrl && selectedPedido && (
          <PDFViewer
            pdfUrl={pdfUrl}
            onClose={handleClosePDF}
            title={`Pedido #${selectedPedido}`}
            documentId={`${selectedPedido}`}
            documentType="pedido"
          />
        )}
      </Box>
  );
};

export default Pedidos;
